import axios from 'axios';
import { config } from '../config';
import selectionStorage from '../utils/selectionStorage';

const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token and partner/chain context
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('idToken');
    if (token) {
      config.headers.Authorization = token;
    }
    // For all /sync/{uuid} routes, append the currently-selected context so the
    // backend can enforce access even for superadmins operating within a specific context.
    if (/\/sync\/[0-9a-f-]{36}/.test(config.url)) {
      // From the PER-TAB store, so a request can never be authorised as a chain this tab is not
      // showing. Reading localStorage here while the screens read React state meant a tab could list
      // one chain's projects and open another chain's project detail.
      const { hotelId, accountId, partnerId } = selectionStorage.getContext();
      config.params = config.params || {};
      if (hotelId)       config.params.hotelId   = hotelId;
      else if (accountId) config.params.accountId = accountId;
      else if (partnerId) config.params.partnerId = partnerId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track in-flight refresh to avoid parallel refresh calls
let refreshPromise = null;

// AN EXPIRED TOKEN IS NOT ALWAYS A 401. The Cognito authorizers answer 401, but the admin API's Lambda
// authorizer (ui-v2-admin-authorizer) answers a token it cannot verify with a Deny policy, which API Gateway
// returns as 403 "User is not authorized to access this resource with an explicit deny". Only 401 was
// refreshed, so an hour after login every Admin screen failed while the rest of the hub carried on.
// A 403 from a handler (a real refusal) carries its own body, never that gateway message, and is left alone.
const isAuthorizerDeny = (error) => {
  if (error.response?.status !== 403) return false;
  const d = error.response.data;
  const msg = typeof d === 'string' ? d : (d && (d.Message || d.message)) || '';
  return /explicit deny/i.test(msg);
};

// Response interceptor — on 401 (or the admin authorizer's Deny), try token refresh then retry once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // The Deny after a refresh is the authorizer meaning it (not a superadmin): reject as it is, no logout.
    if (isAuthorizerDeny(error) && originalRequest._retry) return Promise.reject(error);

    if ((error.response?.status === 401 || isAuthorizerDeny(error)) && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        originalRequest._retry = true;

        try {
          // Serialise concurrent 401s to a single refresh call
          if (!refreshPromise) {
            refreshPromise = axios.post(`${config.apiBaseUrl}/auth/refresh`, { refreshToken })
              .finally(() => { refreshPromise = null; });
          }

          const { data } = await refreshPromise;
          localStorage.setItem('idToken', data.idToken);
          localStorage.setItem('accessToken', data.accessToken);

          originalRequest.headers.Authorization = data.idToken;
          return api(originalRequest);
        } catch {
          // Refresh failed — clear tokens and send to login
        }
      }

      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        const returnPath = window.location.pathname + window.location.search;
        sessionStorage.setItem('loginRedirect', returnPath);
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
